// AISheet — modal para añadir items con IA.
// Soporta:
//  - Prompt de texto en español ("Agrega 5 cafés en Florentin Tel Aviv")
//  - Subir PDF/imagen (ticket, screenshot de reserva)
// Devuelve items estructurados al callback onAdd.
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Upload, Loader2, AlertCircle, Check } from "lucide-react";
import { api } from "../auth/AuthContext";

const ENTITY_LABELS = {
  restaurants: { label: "Restaurantes", icon: "🍽️" },
  places: { label: "Lugares (museos, sinagogas, miradores)", icon: "📍" },
  flights: { label: "Vuelo", icon: "✈️" },
  hotels: { label: "Hotel", icon: "🏨" },
};

const PROMPT_EXAMPLES = {
  restaurants: 'Ej: "Agrega 3 restaurantes vegetarianos en Praga, barrio Vinohrady"',
  places: 'Ej: "5 sinagogas históricas en Budapest no incluidas todavía"',
  flights: 'Ej: "Vuelo IB6831 Tel Aviv→Madrid 15 jun 2026 14:30"',
  hotels: 'Ej: "Hotel Le Méridien Budapest, check-in 31 may, check-out 3 jun"',
};

export default function AISheet({ open, onClose, onAdd, defaultEntity = "restaurants", tripContext = "" }) {
  const [entity, setEntity] = useState(defaultEntity);
  const [mode, setMode] = useState("text"); // text | file
  const [prompt, setPrompt] = useState("");
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null); // {items: [...]}
  const [selected, setSelected] = useState({}); // index -> true
  const fileInputRef = useRef(null);

  const reset = () => {
    setPrompt(""); setFile(null); setNote(""); setError("");
    setPreview(null); setSelected({}); setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const runText = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setError(""); setPreview(null);
    try {
      const r = await api("/api/ai/extract/text", {
        method: "POST",
        body: JSON.stringify({ entity, prompt: prompt.trim(), trip_context: tripContext }),
      });
      const items = r.items || [];
      setPreview({ items });
      // Pre-select all
      const sel = {}; items.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      setError(e.message || "La IA no pudo procesar tu pedido. Intenta reformularlo.");
    } finally {
      setLoading(false);
    }
  };

  const runFile = async () => {
    if (!file) return;
    setLoading(true); setError(""); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("entity", entity);
      fd.append("file", file);
      if (note.trim()) fd.append("note", note.trim());
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = (() => { try { return localStorage.getItem("session_token"); } catch { return null; } })();
      const r = await fetch(`${API}/api/ai/extract/file`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(`Error ${r.status}: ${text || "no se pudo procesar el archivo"}`);
      }
      const data = await r.json();
      const items = data.items || [];
      setPreview({ items });
      const sel = {}; items.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      setError(e.message || "No se pudo leer el archivo.");
    } finally {
      setLoading(false);
    }
  };

  const confirmAdd = () => {
    if (!preview) return;
    const chosen = preview.items.filter((_, i) => selected[i]);
    onAdd?.(entity, chosen);
    handleClose();
  };

  const toggleSel = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1100] bg-black/40 flex items-end justify-center"
          onClick={handleClose}
          data-testid="ai-sheet-backdrop"
        >
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[hsl(var(--bg))] rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
            data-testid="ai-sheet"
          >
            <div className="sticky top-0 bg-[hsl(var(--bg))] z-10 flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--line))]">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-[hsl(var(--accent))]" />
                <h2 className="font-bold">Añadir con IA</h2>
              </div>
              <button onClick={handleClose} className="text-[hsl(var(--muted))]" data-testid="ai-sheet-close">
                <X size={20} />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              {!preview && (
                <>
                  {/* Tipo de entidad */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-1.5">
                      ¿Qué quieres añadir?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                        <button
                          key={k}
                          onClick={() => setEntity(k)}
                          className={`text-left px-3 py-2.5 rounded-xl border text-sm transition-colors ${
                            entity === k
                              ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white"
                              : "bg-[hsl(var(--bg-card))] border-[hsl(var(--line))]"
                          }`}
                          data-testid={`ai-entity-${k}`}
                        >
                          <span className="mr-1.5">{v.icon}</span>
                          <span className="text-xs font-medium">{v.label.split(" ")[0]}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Modo: texto o archivo */}
                  <div className="flex gap-2 border-b border-[hsl(var(--line))]">
                    <button
                      onClick={() => setMode("text")}
                      className={`flex-1 py-2 text-sm font-medium border-b-2 -mb-px ${
                        mode === "text" ? "border-[hsl(var(--accent))] text-[hsl(var(--ink))]" : "border-transparent text-[hsl(var(--muted))]"
                      }`}
                      data-testid="ai-mode-text"
                    >
                      💬 Texto
                    </button>
                    <button
                      onClick={() => setMode("file")}
                      className={`flex-1 py-2 text-sm font-medium border-b-2 -mb-px ${
                        mode === "file" ? "border-[hsl(var(--accent))] text-[hsl(var(--ink))]" : "border-transparent text-[hsl(var(--muted))]"
                      }`}
                      data-testid="ai-mode-file"
                    >
                      📎 PDF / Imagen
                    </button>
                  </div>

                  {mode === "text" && (
                    <div>
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={PROMPT_EXAMPLES[entity] || "Describe qué quieres añadir..."}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-xl border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]"
                        data-testid="ai-prompt-input"
                      />
                      <button
                        onClick={runText}
                        disabled={loading || !prompt.trim()}
                        className="w-full mt-3 py-3 rounded-2xl bg-[hsl(var(--accent))] text-white font-semibold text-sm active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid="ai-generate-text-btn"
                      >
                        {loading ? <><Loader2 size={16} className="animate-spin" /> Generando…</> : <><Sparkles size={16} /> Generar con IA</>}
                      </button>
                    </div>
                  )}

                  {mode === "file" && (
                    <div>
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-2xl border-2 border-dashed border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] p-6 text-center cursor-pointer active:scale-[0.99]"
                        data-testid="ai-file-dropzone"
                      >
                        <Upload size={28} className="mx-auto text-[hsl(var(--muted))] mb-2" />
                        <p className="text-sm font-medium">
                          {file ? file.name : "Toca para subir un archivo"}
                        </p>
                        <p className="text-[11px] text-[hsl(var(--muted))] mt-1">
                          PDF, JPG o PNG · máx 10 MB
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="hidden"
                        data-testid="ai-file-input"
                      />
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Nota opcional para la IA (ej: 'es el vuelo de vuelta')"
                        className="w-full mt-3 px-3 py-2.5 rounded-xl border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]"
                        data-testid="ai-file-note"
                      />
                      <button
                        onClick={runFile}
                        disabled={loading || !file}
                        className="w-full mt-3 py-3 rounded-2xl bg-[hsl(var(--accent))] text-white font-semibold text-sm active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid="ai-generate-file-btn"
                      >
                        {loading ? <><Loader2 size={16} className="animate-spin" /> Analizando…</> : <><Sparkles size={16} /> Leer con IA</>}
                      </button>
                    </div>
                  )}

                  {error && (
                    <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 flex items-start gap-2" data-testid="ai-error">
                      <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700 leading-relaxed">{error}</p>
                    </div>
                  )}
                </>
              )}

              {/* PREVIEW */}
              {preview && (
                <div data-testid="ai-preview">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">
                      Vista previa <span className="text-[hsl(var(--muted))] font-normal">({preview.items.length})</span>
                    </h3>
                    <button onClick={() => { setPreview(null); setSelected({}); }} className="text-xs text-[hsl(var(--accent))] underline" data-testid="ai-back">
                      ← Atrás
                    </button>
                  </div>

                  {preview.items.length === 0 && (
                    <p className="text-sm text-[hsl(var(--muted))] text-center py-6">La IA no encontró resultados. Reformula la búsqueda.</p>
                  )}

                  <ul className="space-y-2">
                    {preview.items.map((it, i) => (
                      <li
                        key={i}
                        onClick={() => toggleSel(i)}
                        className={`card-paper p-3 cursor-pointer transition-colors ${
                          selected[i] ? "ring-2 ring-[hsl(var(--accent))]" : "opacity-60"
                        }`}
                        data-testid={`ai-preview-item-${i}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className={`w-5 h-5 mt-0.5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                            selected[i] ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))]" : "border-[hsl(var(--line))]"
                          }`}>
                            {selected[i] && <Check size={12} className="text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm leading-tight">
                              {it.name || it.airline || `Item ${i + 1}`}
                              {it.flightNumber && <span className="text-[hsl(var(--muted))] font-normal"> · {it.flightNumber}</span>}
                            </p>
                            {(it.cuisine || it.category || it.area) && (
                              <p className="text-xs text-[hsl(var(--muted))] mt-0.5">
                                {[it.cuisine, it.category, it.area, it.city].filter(Boolean).join(" · ")}
                              </p>
                            )}
                            {(it.fromIATA && it.toIATA) && (
                              <p className="text-xs text-[hsl(var(--accent))] mt-0.5 font-mono">
                                {it.fromIATA} → {it.toIATA} · {it.departDate} {it.departTime}
                              </p>
                            )}
                            {it.address && <p className="text-[11px] text-[hsl(var(--muted))] mt-0.5 truncate">{it.address}</p>}
                            {it.tip && <p className="text-[11px] text-[hsl(var(--ink-soft))] mt-1.5 italic">{it.tip}</p>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {preview.items.length > 0 && (
                    <button
                      onClick={confirmAdd}
                      disabled={Object.values(selected).filter(Boolean).length === 0}
                      className="w-full mt-4 py-3 rounded-2xl bg-[hsl(var(--accent))] text-white font-semibold text-sm active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="ai-confirm-add"
                    >
                      <Check size={16} /> Agregar {Object.values(selected).filter(Boolean).length} al viaje
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
